import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import CookieConsent from "@/components/CookieConsent";
import UserMenu from "@/components/UserMenu";
import {
  Phone,
  MapPin,
  Menu,
  X,
  Shield,
  Award,
  PoundSterling,
  ChevronRight,
} from "lucide-react";

export const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/doors", label: "Door Fitting" },
    { path: "/trimwork", label: "Trimwork" },
    { path: "/panelling", label: "Panelling" },
    { path: "/sanding", label: "Sanding" },
    { path: "/gallery", label: "Our Work" },
    { path: "/process", label: "How It Works" },
    { path: "/contact", label: "Get Quote" },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Guarantee Banner - Compact on mobile */}
      <div className="bg-[#2D5A4A] text-white py-2 md:py-2.5 text-center font-medium">
        <div className="max-w-7xl mx-auto px-3 flex flex-wrap justify-center items-center gap-3 sm:gap-6 md:gap-8">
          <span className="flex items-center gap-1.5 text-xs sm:text-sm">
            <Shield className="h-4 w-4 text-amber-300" />
            <span>12-Month Guarantee</span>
          </span>
          <span className="flex items-center gap-1.5 text-xs sm:text-sm">
            <Award className="h-4 w-4 text-amber-300" />
            <span>Price Promise</span>
          </span>
          <span className="flex items-center gap-1.5 text-xs sm:text-sm">
            <PoundSterling className="h-4 w-4 text-amber-300" />
            <span>No VAT</span>
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="bg-black sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16 md:h-20">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_timber-masters-12/artifacts/y4s8pndd_Expert-Carpenter%20logo.png" 
                alt="Expert Carpenter" 
                className="h-10 sm:h-14 md:h-16 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-5">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-medium transition-colors text-sm ${
                    isActive(link.path)
                      ? "text-[#C9A227] border-b-2 border-[#C9A227] pb-1"
                      : "text-white hover:text-[#C9A227]"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <UserMenu />
              <a
                href="tel:07300405045"
                className="flex items-center gap-2 bg-[#C9A227] text-black px-4 py-2.5 rounded-lg hover:bg-[#b8922a] transition-colors font-semibold text-sm"
              >
                <Phone className="h-4 w-4" />
                07300 405045
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-3 -mr-2 min-h-[48px] min-w-[48px] flex items-center justify-center text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-black border-t border-gray-800">
            <div className="px-4 py-3 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block w-full text-left py-4 px-3 rounded-lg text-lg min-h-[52px] flex items-center ${
                    isActive(link.path)
                      ? "text-[#C9A227] font-bold bg-white/5"
                      : "text-white hover:bg-white/10"
                  }`}
                >
                  {link.label}
                  {link.path === "/contact" && (
                    <ChevronRight className="ml-auto h-5 w-5 text-[#C9A227]" />
                  )}
                </Link>
              ))}
              <a
                href="tel:07300405045"
                className="flex items-center justify-center gap-3 bg-[#2D5A4A] text-white font-bold py-4 px-4 rounded-lg text-lg mt-3 min-h-[56px]"
              >
                <Phone className="h-6 w-6" />
                Call 07300 405045
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-1 pb-16 md:pb-0">{children}</main>

      {/* Sticky Mobile CTA Bar - Bottom */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] px-4 py-3 safe-area-bottom">
        <div className="flex gap-3">
          <a
            href="tel:07300405045"
            className="flex-1 flex items-center justify-center gap-2 bg-[#2D5A4A] text-white font-bold py-3 px-4 rounded-lg text-base min-h-[48px]"
          >
            <Phone className="h-5 w-5" />
            Call Now
          </a>
          <Link
            to="/contact"
            className="flex-1 flex items-center justify-center gap-2 bg-[#C9A227] text-black font-bold py-3 px-4 rounded-lg text-base min-h-[48px]"
          >
            Get Quote
          </Link>
        </div>
      </div>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/447300405045?text=Hi%2C%20I%27m%20interested%20in%20getting%20a%20quote%20from%20Expert-Carpenter."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-20 md:bottom-6 right-4 md:right-6 z-50 flex items-center gap-2 bg-green-500 hover:bg-green-600 rounded-full px-4 py-3 shadow-lg hover:shadow-xl transition-all hover:scale-105"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="w-6 h-6 text-white fill-current">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="text-white font-semibold text-sm">WhatsApp</span>
      </a>

      {/* Footer */}
      <footer className="bg-[#2D5A4A] py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center md:text-left">
              <img 
                src="https://customer-assets.emergentagent.com/job_timber-masters-12/artifacts/vzclf3rr_Expert-Carpenter%20logo.png" 
                alt="Expert Carpenter" 
                className="h-12 w-auto mx-auto md:mx-0 mb-4"
              />
              <p className="text-white/80 text-base md:text-sm leading-relaxed">
                Reliable carpentry in Greater Manchester. Door hanging, skirting, architraves, panelling and dust-free floor sanding. 
                Jobs done properly.
              </p>
            </div>
            
            <div className="md:block">
              <h4 className="font-semibold text-white mb-4 text-lg md:text-base text-center md:text-left">Services</h4>
              <ul className="space-y-3 md:space-y-2 text-base md:text-sm text-white/80">
                <li><Link to="/doors" className="hover:text-white block py-1">Door Hanging</Link></li>
                <li><Link to="/trimwork" className="hover:text-white block py-1">Skirting Boards</Link></li>
                <li><Link to="/trimwork" className="hover:text-white block py-1">Architraves</Link></li>
                <li><Link to="/panelling" className="hover:text-white block py-1">Wall Panelling</Link></li>
                <li><Link to="/sanding" className="hover:text-white block py-1">Floor Sanding</Link></li>
              </ul>
            </div>
            
            <div className="md:block">
              <h4 className="font-semibold text-white mb-4 text-lg md:text-base text-center md:text-left">Areas We Cover</h4>
              <ul className="space-y-3 md:space-y-2 text-base md:text-sm text-white/80">
                <li><Link to="/stockport" className="hover:text-white block py-1">Stockport</Link></li>
                <li><Link to="/manchester" className="hover:text-white block py-1">Manchester</Link></li>
                <li><Link to="/cheadle" className="hover:text-white block py-1">Cheadle</Link></li>
                <li><Link to="/sale" className="hover:text-white block py-1">Sale</Link></li>
                <li><Link to="/gallery" className="hover:text-white block py-1">Our Work</Link></li>
              </ul>
            </div>
            
            <div className="text-center md:text-left">
              <h4 className="font-semibold text-white mb-4 text-lg md:text-base">Contact</h4>
              <ul className="space-y-3 md:space-y-2 text-base md:text-sm text-white/80">
                <li>
                  <a href="tel:07300405045" className="flex items-center justify-center md:justify-start gap-2 hover:text-white py-1">
                    <Phone className="h-5 w-5 md:h-4 md:w-4" /> 07300 405045
                  </a>
                </li>
                <li className="flex items-center justify-center md:justify-start gap-2 py-1">
                  <MapPin className="h-5 w-5 md:h-4 md:w-4" /> Greater Manchester
                </li>
              </ul>
            </div>
          </div>
          
          {/* Mobile Footer CTA */}
          <div className="mt-8 md:hidden">
            <Link to="/contact">
              <Button className="w-full h-14 bg-[#C9A227] hover:bg-[#b8922a] text-black font-bold text-lg">
                Get a Free Quote
                <ChevronRight className="ml-2 h-6 w-6" />
              </Button>
            </Link>
          </div>
          
          <div className="mt-8 pt-8 border-t border-white/20 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-white/60">
            <p className="text-center">© {new Date().getFullYear()} Expert-Carpenter.co.uk. All rights reserved.</p>
            <Link to="/admin" className="hover:text-white/80 transition-colors py-2">
              Admin
            </Link>
          </div>
        </div>
      </footer>

      {/* Cookie Consent Banner */}
      <CookieConsent />
    </div>
  );
};

export default Layout;
